import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, CheckCircle, XCircle, Clock, Euro, ShoppingBag, Mail, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import SheetsPanel from '@/components/SheetsPanel';
import ValidateDialog from '@/components/ValidateDialog';

const STATUS = {
  en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  confirmee: { label: 'Confirmée', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  refusee: { label: 'Non retenue', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  annulee: { label: 'Annulée', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  retiree: { label: 'Retirée', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
};
const UNIVERS = { tcg: { label: 'TCG', cls: 'bg-violet-100 text-violet-700 border-violet-200' }, jeu_de_societe: { label: 'Jeu', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }, bd_manga: { label: 'BD & Manga', cls: 'bg-rose-100 text-rose-700 border-rose-200' } };
const finalQty = (oi) => oi.adjusted_quantity != null ? oi.adjusted_quantity : oi.quantity;

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '', product_type: '' });
  const [exporting, setExporting] = useState(false);
  const [validating, setValidating] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return api.get(`/admin/preorders?${p.toString()}`).then(r => setOrders(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  const validate = (po) => setValidating(po);
  const refuse = async (po) => {
    if (!window.confirm(`Ne pas retenir la demande de ${po.first_name} ${po.last_name} ? Un email « quantités limitées » lui sera envoyé.`)) return;
    const r = await api.put(`/admin/preorders/${po.id}/refuse`);
    toast.success(r.data.email_sent ? "Non retenue, email envoyé" : "Non retenue (email non envoyé)"); load();
  };
  const setStatus = async (po, status) => {
    if (status === 'confirmee' && po.status !== 'confirmee') { setValidating(po); return; }
    if (status === 'refusee' && po.status !== 'refusee' && !window.confirm(`Marquer la demande de ${po.first_name} ${po.last_name} comme non retenue ? Un email « quantités limitées » lui sera envoyé.`)) return;
    try {
      const r = await api.put(`/admin/preorders/${po.id}/status`, { status });
      const mail = r.data.email_sent === true ? ' · email envoyé' : r.data.email_sent === false ? ' · email NON envoyé' : '';
      toast.success(`Statut : ${STATUS[status].label}${mail}`); load();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get('/admin/preorders/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `commandes-plume-et-pion.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); } finally { setExporting(false); }
  };

  const stats = {
    pending: orders.filter(o => o.status === 'en_attente').length,
    confirmed: orders.filter(o => o.status === 'confirmee').length,
    refused: orders.filter(o => o.status === 'refusee').length,
    revenue: orders.filter(o => o.status === 'confirmee').reduce((s, o) => s + (o.total || 0), 0),
  };
  const fmtDate = (d) => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Suivi des commandes</h1>
          <p className="text-slate-500 mt-1">Toutes les précommandes, tous univers, en temps réel</p>
        </div>
        <Button onClick={exportXlsx} disabled={exporting} className="rounded-full" data-testid="export-xlsx-btn"><Download className="h-4 w-4 mr-2" /> {exporting ? 'Export...' : 'Exporter Excel'}</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><Clock className="h-4 w-4 text-amber-500" /> En attente</div><p className="text-2xl font-bold mt-1" data-testid="stat-pending">{stats.pending}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><CheckCircle className="h-4 w-4 text-emerald-500" /> Confirmées</div><p className="text-2xl font-bold mt-1" data-testid="stat-confirmed">{stats.confirmed}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><XCircle className="h-4 w-4 text-rose-500" /> Non retenues</div><p className="text-2xl font-bold mt-1" data-testid="stat-refused">{stats.refused}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><div className="flex items-center gap-2 text-slate-500 text-sm"><Euro className="h-4 w-4 text-violet-500" /> CA confirmé</div><p className="text-2xl font-bold mt-1 text-violet-600" data-testid="stat-revenue">{stats.revenue.toFixed(2)} €</p></div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-4">
          <div className="flex flex-wrap gap-3 p-4 bg-white rounded-2xl border border-slate-200" data-testid="orders-filters">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="Nom, email, extension..." className="pl-9 rounded-lg" data-testid="orders-search-input" />
            </div>
            <Select value={filters.product_type || 'all'} onValueChange={v => setFilters(f => ({ ...f, product_type: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-[160px] rounded-lg" data-testid="orders-univers-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tous univers</SelectItem><SelectItem value="tcg">TCG</SelectItem><SelectItem value="jeu_de_societe">Jeux de société</SelectItem><SelectItem value="bd_manga">BD & Manga</SelectItem></SelectContent>
            </Select>
            <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-[160px] rounded-lg" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tous statuts</SelectItem>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {orders.length === 0 ? (
              <div className="text-center py-16" data-testid="orders-empty"><ShoppingBag className="h-10 w-10 mx-auto text-slate-200" /><p className="mt-3 text-slate-500 text-sm">Aucune commande</p></div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead>Commande</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {orders.map(po => {
                      const u = UNIVERS[po.product_type] || UNIVERS.tcg;
                      return (
                        <TableRow key={po.id} className={po.status === 'en_attente' ? 'bg-amber-50/40' : ''} data-testid={`order-row-${po.id}`}>
                          <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(po.created_at)}</TableCell>
                          <TableCell>
                            <p className="font-medium text-slate-900 text-sm">{po.first_name} {po.last_name}</p>
                            <p className="text-xs text-slate-500">{po.email}</p>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 mb-1"><Badge className={`text-[10px] border ${u.cls}`}>{u.label}</Badge><Link to={`/admin/extensions/${po.extension_id}`} className="text-sm font-medium text-slate-800 hover:text-violet-600">{po.extension_name}</Link></div>
                            <p className="text-xs text-slate-500">{po.items.map(oi => `${oi.item_name} x${finalQty(oi)}`).join(', ')}</p>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-violet-600 whitespace-nowrap">{(po.total || 0).toFixed(2)} €</TableCell>
                          <TableCell>
                            <Select value={po.status} onValueChange={v => setStatus(po, v)}>
                              <SelectTrigger className="h-8 w-[130px] rounded-lg text-xs border-0 p-0 shadow-none focus:ring-0" data-testid={`order-status-${po.id}`}>
                                <Badge className={`text-xs border ${STATUS[po.status]?.cls || ''}`}>{STATUS[po.status]?.label || po.status}</Badge>
                              </SelectTrigger>
                              <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                            </Select>
                            {po.emails?.length > 0 && <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><Mail className="h-3 w-3" /> {po.emails.length} email{po.emails.length > 1 ? 's' : ''}</p>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {po.status === 'en_attente' && <>
                                <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => validate(po)} title="Valider + email" data-testid={`order-validate-${po.id}`}><CheckCircle className="h-4 w-4" /></Button>
                                <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-rose-600 hover:bg-rose-50" onClick={() => refuse(po)} title="Non retenu + email" data-testid={`order-refuse-${po.id}`}><XCircle className="h-4 w-4" /></Button>
                              </>}
                              {po.status === 'confirmee' && <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-violet-600 hover:bg-violet-50" onClick={async () => { if (!window.confirm(`Renvoyer l'email de confirmation à ${po.email} ?`)) return; const r = await api.put(`/admin/preorders/${po.id}/validate`); toast.success(r.data.email_sent ? 'Email de confirmation renvoyé' : "Email non envoyé (réessayez dans quelques minutes)"); load(); }} title="Renvoyer l'email de confirmation" data-testid={`order-resend-${po.id}`}><Mail className="h-4 w-4" /></Button>}
                              <Link to={`/admin/extensions/${po.extension_id}`}><Button size="sm" variant="ghost" className="h-8 rounded-full text-slate-400 hover:text-slate-900" title="Voir l'extension" data-testid={`order-open-${po.id}`}><Eye className="h-4 w-4" /></Button></Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400">{orders.length} commande{orders.length > 1 ? 's' : ''} · actualisation automatique toutes les 30 s</p>
        </div>

        <div className="xl:col-span-4">
          <SheetsPanel />
        </div>
      </div>
      <ValidateDialog order={validating} open={!!validating} onClose={() => setValidating(null)} onDone={load} />
    </div>
  );
}
